import { useEffect, useId, useRef, useState } from 'react';

const PHONE = '(max-width: 767px)';

/** Ids of the overlays recorded in the current history entry, innermost last. */
function stackOf(state) {
  return Array.isArray(state?.__overlays) ? state.__overlays : [];
}

/**
 * On a phone, the system back button (or the back swipe) closes the open
 * sheet, drawer or dialog instead of leaving the page - what people expect
 * from an app.
 *
 * Opening adds a history entry for the same URL that names this overlay;
 * Back pops it, and the overlay closes. Closing any other way (X, the dimmed
 * page, Escape) takes the entry back off, so the next Back still leaves the
 * page. The existing state is kept on the entry, so React Router sees the
 * same location and key. Overlays opened inside one another stack.
 *
 * Returns true while the entry is in place. Links inside the overlay should
 * then navigate with `replace`, so the new page takes the overlay's entry
 * rather than burying it - otherwise Back from that page lands on the old one
 * with nothing open, and needs a second press.
 *
 * Desktop is left alone: there the browser's Back means "previous page".
 */
export function useBackToClose(open, onClose) {
  const id = useId();
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const [pushed, setPushed] = useState(false);

  useEffect(() => {
    if (!open || !window.matchMedia?.(PHONE).matches) return undefined;

    const state = window.history.state;
    window.history.pushState({ ...state, __overlays: [...stackOf(state), id] }, '');
    setPushed(true);

    const onPop = (event) => {
      if (!stackOf(event.state).includes(id)) closeRef.current?.();
    };
    window.addEventListener('popstate', onPop);

    return () => {
      window.removeEventListener('popstate', onPop);
      setPushed(false);
      // Still on top: closed by the page, not by Back, so take the entry off.
      // Otherwise Back already popped it, or a navigation went on past it.
      const stack = stackOf(window.history.state);
      if (stack[stack.length - 1] === id) window.history.back();
    };
  }, [open, id]);

  return pushed;
}
